interface SafeStorageOptions {
  onError?: (error: unknown, context: { op: string; key?: string }) => void
}

type StorageFactory = () => Storage | null

interface SafeStorage {
  // String API
  getString(key: string): string | null
  setString(key: string, value: string): boolean

  // JSON API
  getJSON<T>(key: string): T | null
  setJSON<T>(key: string, value: T): boolean

  /** Number of keys currently in storage */
  length(): number

  /** Returns all keys in storage */
  keys(): string[]

  /** Returns the key name at the provided index */
  key(index: number): string | null

  remove(key: string): boolean
  clear(): boolean
}

export function createSafeStorage(
  storageFactory: StorageFactory,
  options: SafeStorageOptions = {},
): SafeStorage {
  const report = (error: unknown, context: { op: string; key?: string }) => {
    try {
      options.onError?.(error, context)
    } catch {
      // never throw from error reporting
    }
  }

  const getStorage = () => {
    try {
      return storageFactory()
      // oxlint-disable-next-line unicorn/catch-error-name
    } catch (err) {
      report(err, { op: 'getStorage' })
      return null
    }
  }

  const getString = (key: string): string | null => {
    try {
      const storage = getStorage()
      if (!storage) {
        return null
      }
      return storage.getItem(key)
      // oxlint-disable-next-line unicorn/catch-error-name
    } catch (err) {
      report(err, { op: 'getItem', key })
      return null
    }
  }

  const setString = (key: string, value: string): boolean => {
    try {
      const storage = getStorage()
      if (!storage) {
        return false
      }
      storage.setItem(key, value)
      return true
      // oxlint-disable-next-line unicorn/catch-error-name
    } catch (err) {
      report(err, { op: 'setItem', key })
      return false
    }
  }

  const getJSON = <T>(key: string): T | null => {
    const raw = getString(key)
    if (raw === null) {
      return null
    }

    try {
      return JSON.parse(raw) as T
      // oxlint-disable-next-line unicorn/catch-error-name
    } catch (err) {
      report(err, { op: 'jsonParse', key })
      return null
    }
  }

  const setJSON = <T>(key: string, value: T): boolean => {
    try {
      return setString(key, JSON.stringify(value))
      // oxlint-disable-next-line unicorn/catch-error-name
    } catch (err) {
      report(err, { op: 'jsonStringify', key })
      return false
    }
  }

  const length = (): number => {
    try {
      const storage = getStorage()
      if (!storage) {
        return 0
      }
      return storage.length
      // oxlint-disable-next-line unicorn/catch-error-name
    } catch (err) {
      report(err, { op: 'length' })
      return 0
    }
  }

  const key = (index: number): string | null => {
    try {
      const storage = getStorage()
      if (!storage) {
        return null
      }
      return storage.key(index)
      // oxlint-disable-next-line unicorn/catch-error-name
    } catch (err) {
      report(err, { op: 'key' })
      return null
    }
  }

  const keys = (): string[] => {
    try {
      const storage = getStorage()
      if (!storage) {
        return []
      }

      const result: string[] = []

      for (let i = 0; i < storage.length; i++) {
        const k = storage.key(i)
        if (k !== null) {
          result.push(k)
        }
      }
      return result
      // oxlint-disable-next-line unicorn/catch-error-name
    } catch (err) {
      report(err, { op: 'keys' })
      return []
    }
  }
  const remove = (key: string): boolean => {
    try {
      const storage = getStorage()
      if (!storage) {
        return false
      }
      storage.removeItem(key)
      return true
      // oxlint-disable-next-line unicorn/catch-error-name
    } catch (err) {
      report(err, { op: 'removeItem', key })
      return false
    }
  }

  const clear = (): boolean => {
    try {
      const storage = getStorage()
      if (!storage) {
        return false
      }
      storage.clear()
      return true
      // oxlint-disable-next-line unicorn/catch-error-name
    } catch (err) {
      report(err, { op: 'clear' })
      return false
    }
  }

  return {
    getString,
    setString,
    getJSON,
    setJSON,
    length,
    key,
    keys,
    remove,
    clear,
  }
}

const isDev = process.env.NODE_ENV !== 'production'

const onError = isDev
  ? (err: unknown, ctx: { op: string; key?: string }) => {
      // eslint-disable-next-line no-console
      console.warn(`[storage] ${ctx.op}${ctx.key ? `(${ctx.key})` : ''}`, err)
    }
  : undefined

export const localStore = createSafeStorage(
  () => {
    try {
      // oxlint-disable-next-line no-negated-condition
      return typeof window !== 'undefined' ? window.localStorage : null
    } catch {
      return null
    }
  },
  { onError },
)

export const sessionStore = createSafeStorage(
  () => {
    try {
      // oxlint-disable-next-line no-negated-condition
      return typeof window !== 'undefined' ? window.sessionStorage : null
    } catch {
      return null
    }
  },
  { onError },
)
